import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, TrendingUp, Zap, User } from 'lucide-react';

interface BahisHareketi {
  id: string;
  username: string;
  game: string;
  betAmount: number;
  winAmount: number;
  multiplier: number;
  createdAt: Date;
}

// Demo veriler - SarLab'da gercek zamanli bahis verisi yok
const demoBahisler: BahisHareketi[] = [
  { id: '1', username: 'Aly***', game: 'Sweet Bonanza', betAmount: 50, winAmount: 750, multiplier: 15, createdAt: new Date() },
  { id: '2', username: 'Meh***', game: 'Gates of Olympus', betAmount: 100, winAmount: 2500, multiplier: 25, createdAt: new Date() },
  { id: '3', username: 'Can***', game: 'Big Bass Bonanza', betAmount: 25, winAmount: 375, multiplier: 15, createdAt: new Date() },
  { id: '4', username: 'Emu***', game: 'Starlight Princess', betAmount: 200, winAmount: 8000, multiplier: 40, createdAt: new Date() },
  { id: '5', username: 'Sel***', game: 'Sugar Rush', betAmount: 75, winAmount: 1125, multiplier: 15, createdAt: new Date() },
  { id: '6', username: 'Oya***', game: 'The Dog House', betAmount: 150, winAmount: 4500, multiplier: 30, createdAt: new Date() },
  { id: '7', username: 'Ber***', game: 'Crazy Time', betAmount: 500, winAmount: 25000, multiplier: 50, createdAt: new Date() },
  { id: '8', username: 'Yus***', game: 'Lightning Roulette', betAmount: 80, winAmount: 2800, multiplier: 35, createdAt: new Date() },
  { id: '9', username: 'Cem***', game: 'Fruit Party', betAmount: 60, winAmount: 900, multiplier: 15, createdAt: new Date() },
  { id: '10', username: 'Den***', game: 'Wild West Gold', betAmount: 120, winAmount: 3600, multiplier: 30, createdAt: new Date() },
  { id: '11', username: 'Kar***', game: 'Book of Dead', betAmount: 45, winAmount: 900, multiplier: 20, createdAt: new Date() },
  { id: '12', username: 'Fat***', game: 'Reactoonz', betAmount: 90, winAmount: 1800, multiplier: 20, createdAt: new Date() },
  { id: '13', username: 'Gok***', game: 'Mega Ball', betAmount: 250, winAmount: 12500, multiplier: 50, createdAt: new Date() },
  { id: '14', username: 'Hak***', game: 'Monopoly Live', betAmount: 100, winAmount: 2000, multiplier: 20, createdAt: new Date() },
  { id: '15', username: 'Ibr***', game: 'Gonzo Quest', betAmount: 35, winAmount: 700, multiplier: 20, createdAt: new Date() },
  { id: '16', username: 'Jas***', game: 'Starburst', betAmount: 40, winAmount: 400, multiplier: 10, createdAt: new Date() },
  { id: '17', username: 'Kor***', game: 'Dead or Alive 2', betAmount: 180, winAmount: 9000, multiplier: 50, createdAt: new Date() },
  { id: '18', username: 'Lev***', game: 'Buffalo King', betAmount: 65, winAmount: 1300, multiplier: 20, createdAt: new Date() },
  { id: '19', username: 'Mur***', game: 'Wolf Gold', betAmount: 55, winAmount: 825, multiplier: 15, createdAt: new Date() },
  { id: '20', username: 'Ned***', game: 'Jammin Jars', betAmount: 300, winAmount: 15000, multiplier: 50, createdAt: new Date() },
];

const CanliBahisHareketleri: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevVisibleIds, setPrevVisibleIds] = useState<Set<string>>(new Set());

  // Demo verileri kullan - SarLab'da gercek zamanli bahis verisi yok
  useEffect(() => {
    // Simule loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const displayData = useMemo(() => demoBahisler, []);

  // visibleItems - 6 öğe göster (yeni en üstte)
  const visibleItems = useMemo(() => {
    const items: (BahisHareketi & { position: number; isNew: boolean })[] = [];
    const currentIds = new Set<string>();

    for (let i = 0; i < 6; i++) {
      const dataIndex = (currentIndex - i + displayData.length) % displayData.length;
      const item = displayData[dataIndex];
      currentIds.add(item.id);
      items.push({
        ...item,
        position: i,
        isNew: !prevVisibleIds.has(item.id)
      });
    }

    setPrevVisibleIds(currentIds);
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, displayData]);

  // Otomatik kaydırma - 0.3-0.8 saniye arası random
  useEffect(() => {
    const dataLength = displayData.length;

    const scheduleNext = () => {
      const randomDelay = Math.random() * 500 + 300;
      return setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % dataLength);
        timeoutRef.current = scheduleNext();
      }, randomDelay);
    };

    const timeoutRef = { current: scheduleNext() };

    return () => clearTimeout(timeoutRef.current);
  }, [displayData.length]);

  // Çarpan rengini belirle
  const getMultiplierColor = (multiplier: number) => {
    if (multiplier >= 50) return 'text-yellow-400 bg-yellow-500/20';
    if (multiplier >= 30) return 'text-purple-400 bg-purple-500/20';
    if (multiplier >= 20) return 'text-blue-400 bg-blue-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1" />
        <div className="flex flex-col items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Canlı Bahis Hareketleri</h2>
          </div>
          <p className="text-gray-400 text-xs mt-1">Şu an gerçekleşen bahis hareketlerini takip edin</p>
        </div>
        <div className="flex-1 flex justify-end">
          <div className="flex items-center space-x-1">
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            <span className="text-blue-400 text-xs">Canlı</span>
          </div>
        </div>
      </div>

      <div className="relative min-h-[336px] overflow-hidden">
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-14 bg-dark-700/50 rounded-lg" />
            ))}
          </div>
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
            {visibleItems.map((bahis) => (
              <motion.div
                key={bahis.id}
                layout
                initial={bahis.isNew ? { opacity: 0, y: -60, scale: 0.9 } : false}
                animate={{
                  opacity: bahis.position === 5 ? 0.4 : 1,
                  y: bahis.position * 56,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  y: 380,
                  scale: 0.9,
                  transition: { duration: 0.3 }
                }}
                transition={{
                  y: { type: "spring", stiffness: 350, damping: 30, mass: 0.8 },
                  opacity: { duration: 0.2 },
                  scale: { type: "spring", stiffness: 350, damping: 30 }
                }}
                className="absolute left-0 right-0 flex items-center justify-between p-3 bg-dark-700/30 rounded-lg hover:bg-dark-700/50 transition-colors"
                style={{ height: '52px' }}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium text-sm truncate">{bahis.username}</p>
                    <p className="text-gray-400 text-xs truncate">{bahis.game}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 flex-shrink-0">
                  {/* Çarpan */}
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-md ${getMultiplierColor(bahis.multiplier)}`}>
                    <Zap className="w-3 h-3" />
                    <span className="text-xs font-bold">{bahis.multiplier}x</span>
                  </div>

                  {/* Bahis ve Kazanç */}
                  <div className="text-right">
                    <div className="flex items-center space-x-1 text-green-400 font-bold">
                      <TrendingUp className="w-3 h-3" />
                      <span className="text-sm">{bahis.winAmount.toLocaleString('tr-TR')} ₺</span>
                    </div>
                    <p className="text-gray-500 text-xs">{bahis.betAmount.toLocaleString('tr-TR')} ₺ bahis</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default CanliBahisHareketleri;
